/*
 * The StepBudgetCustomer component is the step in reseller SO flow enabling customer budget configuration.
 * 
 * This takes the following props:
 *   auth                     - authentication object
 *   history                  - for navigation to non-SO flow.  TODO: refactor when moving to react router 6, consider removing functionality
 *   customer                 - customer for which this SO is being created
 *   project                  - project for which this SO is being created
 *   assignedStep
 *   activeStep
 *   handleBack
 *   handleNext
 */

import React from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';

import PropTypes from 'prop-types';
import AbEnvironment from '../auth/AbEnvironment';

import { styled } from '@mui/material/styles';
import withStyles from '@mui/styles/withStyles';
import { Accordion, AccordionSummary, AccordionDetails, Button, Divider, FormControlLabel, Grid, 
         Paper, Switch, TableContainer, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { tableCellClasses } from '@mui/material/TableCell';
import AppBindStyles from '../../AppBindStyles';
import { getValueForRender, getNumericValueForRender } from '../../utils/InputUtils';
import HelpOutline from '@mui/icons-material/HelpOutline';

import SubscriptionTableRow from './SubscriptionTableRow';
import CurrencyTextField from '../ui/CurrencyTextField';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import ChangeHistory from '@mui/icons-material/ChangeHistory';
import SettingsBackupRestore from '@mui/icons-material/SettingsBackupRestore';

import RequestedBudgetAccountUpdateMutation from '../paymentsources/RequestedBudgetAccountUpdateMutation'
import RequestedBudgetAccountUnsetMutation from '../paymentsources/RequestedBudgetAccountUnsetMutation'
import RequestedBudgetWalletUpdateMutation from '../paymentsources/RequestedBudgetWalletUpdateMutation'
import RequestedBudgetProjectUpdateMutation from '../paymentsources/RequestedBudgetProjectUpdateMutation'
import RequestedBudgetWalletUnsetMutation from '../paymentsources/RequestedBudgetWalletUnsetMutation'

import LoadingSpinner from '../ui/LoadingSpinner';
import SavingSpinner from '../ui/SavingSpinner';
import ServerError from '../ui/ServerError';

const abEnvironment = new AbEnvironment();
const StepBudgetCustomerQuery = graphql`
query StepBudgetCustomerQuery($accountId: UUID!, $projectId: UUID) {
  accountTByAccountId(accountId: $accountId) {
    accountId
    accountNum
    accountName
    contactLastName
    contactFirstName
    contactEmail
    contactPhone
    address1
    address2
    city
    state
    country
    postalCode
    useBudgets
    initialBalanceAmount
    rAutoTopupActive
    autoTopupActive
    rAutoTopupAmount
    autoTopupAmount
    rAutoTopupThreshold
    autoTopupThreshhold
    rMaxMonthlyBudget
    maxMonthlyBudget
    rMaxCharge
    maxCharge
    budgetApprovedCount
    rPreApproveSubs
    preApproveSubs
    accountTByParentId {
      payFromBalance
      autoTopupActive
      autoTopupAmount
      autoTopupThreshhold
      initialBalanceAmount 
      paymentSourceTsByAccountId {
        totalCount
        nodes {
          psType
        }
      }
    }
    projectTsByCustomerAccountId(condition: {projectId: $projectId}) {
      totalCount
      nodes {
        projectId
        projectName
        preApproveSubs
        rPreApproveSubs
        budgetApprovedCount
        walletTByCustomerWalletId {
          walletId
          initialBalanceAmount
          rAutoTopupActive
          autoTopupActive
          rAutoTopupAmount
          autoTopupAmount
          rAutoTopupThreshold
          autoTopupThreshold
          rMaxMonthlyBudget
          maxMonthlyBudget
          rMaxCharge
        }
      }
    }
    customerAppTsByCustomerAccountId(condition: {projectId: $projectId}) {
      totalCount
      nodes {
        customerAppId
        appId
        customerAccountId
        accountTByCustomerAccountId {
          accountName
          contactFirstName
          contactLastName
          contactEmail
          accountType
          accountTByParentId {
            accountName
            contactFirstName
            contactLastName
            contactEmail
          }
          userTsByAccountId {
            nodes {
              accountLinkTsByUserId {
                totalCount
              }
            }
          }
          paymentSourceTsByAccountId {
            totalCount
          }
        }
        appStatus
        bindCardTByBcId {
          bcStatus
        }
        appSkuPrice
        appMarkupAbsolute
        appMarkupPercentage
        paymentOrder
        passThroughFees
        rAppMarkupPercentage
        rPaymentOrder
        rPassThroughFees
        bcId
        sharedEmail
        showSubscription
        passThroughFees
        appTByAppId {
          appName
          appSku
          appUrl
          appLogo
          appType
          description
          signupUrl
        }
      }
    }
  }
}
`;

const styleSheet = theme => ({
  root: {
    flexGrow: 1,
  },
  paper: {
    padding: theme.spacing(3),
    textAlign: 'left',
    // maxWidth: 980
  },
  accountLogo: {
    float: 'left',
    marginRight: 15
  },
  button: {
    width: 52,
  },
  currencyField3: {
    width: 80,
  },
  currencyLabelField: {
    width: 130,
    marginTop: 10,
    marginBottom: 4,
    // marginRight: 8,
  },
  input: {
    display: 'none',
  },
  noAddFundsHolder: {
    width: 84,
    //marginTop: 12,
    //marginBottom: 8,
    marginRight: 4,
    textAlign: 'right',
  },
  noBudgetHolder: {
    width: 84,
    marginTop: 4,
    //marginBottom: 8,
    marginRight: 4,
    textAlign: 'right',
  },
  newAppRow: {
    // backgroundColor: yellow[100],
    borderLeft: 'solid',
    borderLeftWidth: 3,
    borderLeftColor: '#81C784;',
  },
  termsChangedRow: {
    borderLeft: 'dotted',
    borderLeftWidth: 3,
    borderLeftColor: '#FFBF00;'
  },
  unchangedRow: {
    marginLeft: 3
  },
  existingAppRow: {
    marginLeft: 6
  },
  terms: {
    maxWidth: 170,
    minWidth: 170
  }
});

const StyledTable = styled(Table) (({ theme }) => ({
  borderWidth: 1,
  borderStyle: 'solid',
  //borderColor: theme.palette.colors.secondary[200],
  borderColor: theme.palette.divider,
  borderTopLeftRadius: theme.spacing(1),
  borderTopRightRadius: theme.spacing(1),
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    // backgroundColor: theme.palette.common.black,
    // color: theme.palette.common.white,
    // backgroundColor: theme.palette.colors.secondary[200],
    backgroundColor: theme.palette.divider,
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  /*
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  */
  height: 50, // Make the row heights consistent.  Smaller than this could be better, but then padding needs to be adjusted.
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

function ValueLabelComponent(props) {
  const { children, open, value } = props;

  let formattedValue = Number(value).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2})

  return (
    <Tooltip open={open} enterTouchDelay={0} placement="bottom" title={formattedValue}>
      {children}
    </Tooltip>
  );
}

ValueLabelComponent.propTypes = {
  children: PropTypes.element.isRequired,
  open: PropTypes.bool.isRequired,
  value: PropTypes.number.isRequired,
};

function ValueLabelFormat(value) {
  let theVal = value;
  if (isNaN(value))
    theVal = 0;

  return Number(theVal).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 0});
}

ValueLabelFormat.propTypes = {
  value: PropTypes.number.isRequired,
};

class StepBudgetCustomer extends React.Component {
  
  state = {
    firstName:  null,
    lastName:   null,
    country:    null,
    state:      null,
    city:       null,
    postalCode: null,
    address1:   null,
    address2:   null,
    rMaxMonthlyBudgetSet: false, // Workaround to indicate if the state value is null or unset
    initialBalanceAmount: null,  // In a new SO, this represents the target initial balance.  In a follow-up SO, this represents a one-time fee request.
    oneTimeFeeRequestSet: false, // For follow-up SOs, whether or not we're looking for a one-time fee request
    rAutoTopupActive: null,
    rAutoTopupThreshold: null,
    rAutoTopupAmount: null,
    rMaxCharge: 9900,
    rMaxMonthlyBudget: null,
    origRAutoTopupActive: null,
    origRAutoTopupAmount: null,
    origRAutoTopupThreshold: null,

    showSpinner: false,

    accepted:    false,

    forceFetch: 1
  }

  isBackDisabled() {
    if (this.props.activeStep === 0)
      return true;

    return false;
  }

  /* Determine if a value has changed */
  hasNumericValueChanged(state_member, prop_member) {
    if (state_member != null && Number(state_member) !== Number(prop_member)) {
      return true;
    }
    return false;
  }

  hasValueChanged(state_member, prop_member) {
    if (state_member != null && state_member !== prop_member) {
      return true;
    }
    return false;
  }

  /***************************************
   * Specialized get value functions to
   * get auto topup values
   **************************************/
  getTopupValue(stateRValue, savedRValue, savedValue, defaultValue) {
    let theReturn = null;
    if (stateRValue != null) {
      theReturn = stateRValue;
    } else if (savedRValue != null) {
      theReturn = savedRValue;
    } else if (savedValue != null && Number(savedValue) !== 0) {
      theReturn = savedValue;
    } else if (defaultValue != null) {
      theReturn = defaultValue;
    } else {
      return null;
    }

    return Number(theReturn);
  }

  /******************************
   * Validation functions       *
   ******************************/

  /*
   * This is a bit complex, as we're presenting initial balance amount
   * slightly differently on first time (initial balance) use vs. follow-up (one-time fee) use.
   */
  isInitialBalanceAmountValid(theAccount, theProject, theWallet) {
    const { getInitialBalanceAmountMinimum, getOneTimeBalanceAmountMinimum, getInitialBalanceAmountLimit, getInitialBalanceAmountDefault } = this.props.auth;

    // If project and wallet are set, we're doing project funding.  Otherwise, account funding.
    if (theProject != null && theWallet != null) {
      if (theProject.budgetApprovedCount === 0) {
        // Get the current initial balance amount
        let val = getNumericValueForRender(this.state.initialBalanceAmount, theWallet.initialBalanceAmount, getInitialBalanceAmountDefault()); 
        if (val < getInitialBalanceAmountMinimum() || val > getInitialBalanceAmountLimit()) {
          return false;
        }
      } else {
        // Get the one time fee request
        let val = getNumericValueForRender(this.state.initialBalanceAmount, theWallet.initialBalanceAmount, getInitialBalanceAmountDefault()); 
        if (val < getOneTimeBalanceAmountMinimum() || val > getInitialBalanceAmountLimit()) {
          return false;
        } 
      }  
    } else {
      if (theAccount.budgetApprovedCount === 0) {
        // Get the current initial balance amount
        let val = getNumericValueForRender(this.state.initialBalanceAmount, theAccount.initialBalanceAmount, getInitialBalanceAmountDefault()); 
        if (val < getInitialBalanceAmountMinimum() || val > getInitialBalanceAmountLimit()) {
          return false;
        }
      } else {
        // Get the one time fee request
        let val = getNumericValueForRender(this.state.initialBalanceAmount, theAccount.initialBalanceAmount, getInitialBalanceAmountDefault()); 
        if (val < getOneTimeBalanceAmountMinimum() || val > getInitialBalanceAmountLimit()) {
            return false;
        }
      }
    }
    return true;
  }

  isThresholdValid(theAccount, theProject, theWallet) {
    const { getTopupThresholdMinimum, getTopupThresholdDefault, getTopupThresholdLimit, getInitialBalanceAmountDefault } = this.props.auth;

    // If project and wallet are set, we're doing project funding.  Otherwise, account funding.
    if (theProject != null && theWallet != null) {
      let val = this.getTopupValue(this.state.rAutoTopupThreshold, theWallet.rAutoTopupThreshold, theWallet.autoTopupThreshold, getTopupThresholdDefault()); 
      if (val == null || val < getTopupThresholdMinimum() || val > getTopupThresholdLimit()) {
        return false;
      }

      // If FTU, don't allow auto-topup threshold to be greater than the initial value
      if (theProject.budgetApprovedCount === 0) {
        let theInitialValue = getNumericValueForRender(this.state.initialBalanceAmount, theWallet.initialBalanceAmount, getInitialBalanceAmountDefault()); 
        if (theInitialValue == null || val > theInitialValue) {
          return false;
        }
      }
    } else { 
      let val = this.getTopupValue(this.state.rAutoTopupThreshold, theAccount.rAutoTopupThreshold, theAccount.autoTopupThreshhold,  getTopupThresholdDefault()); 
      if (val == null || val < getTopupThresholdMinimum() || val > getTopupThresholdLimit()) {
        return false;
      }

      // If FTU, don't allow auto-topup threshold to be greater than the initial value
      if (theAccount.budgetApprovedCount === 0) {
        let theInitialValue = getNumericValueForRender(this.state.initialBalanceAmount, theAccount.initialBalanceAmount, getInitialBalanceAmountDefault()); 
        if (theInitialValue == null || val > theInitialValue) {
          return false;
        }
      }
    }

    return true;
  }

  getThresholdErrorMessage(theAccount, theProject, theWallet) {
    const { getTopupThresholdDefault, getInitialBalanceAmountDefault } = this.props.auth;
    let errMesg = null;

    // If project and wallet are set, we're doing project funding.  Otherwise, account funding.
    if (theProject != null && theWallet != null) {
      if (theProject.budgetApprovedCount === 0) {
        let thresholdVal = this.getTopupValue(this.state.rAutoTopupThreshold, theWallet.rAutoTopupThreshold, theWallet.AutoTopupThreshold, getTopupThresholdDefault()); 
        let initialVal = getNumericValueForRender(this.state.initialBalanceAmount, theWallet.initialBalanceAmount, getInitialBalanceAmountDefault()); 
        if (initialVal < thresholdVal) {
          errMesg = 'Cannot be higher than initial value';
        }
      }
    } else {
      if (theAccount.budgetApprovedCount === 0) {
        let thresholdVal = this.getTopupValue(this.state.rAutoTopupThreshold, theAccount.rAutoTopupThreshold, theAccount.AutoTopupThreshhold, getTopupThresholdDefault()); 
        let initialVal = getNumericValueForRender(this.state.initialBalanceAmount, theAccount.initialBalanceAmount, getInitialBalanceAmountDefault()); 
        if (initialVal < thresholdVal) {
          errMesg = 'Cannot be higher than initial value';
        }
      }
    }

    return errMesg;
  }

  isThresholdError(theAccount, theProject, theWallet) {
    if (this.getThresholdErrorMessage(theAccount, theProject, theWallet) == null) {
      return false;
    }
    return true;
  }

  isAmountValid(theAccount, theWallet) {
    const { getTopupTargetMinimum, getTopupTargetDefault, getTopupTargetLimit, getTopupThresholdDefault  } = this.props.auth;

    // If wallet is set, we're doing project funding.  Otherwise, account funding.
    if (theWallet != null) {
      let val = this.getTopupValue(this.state.rAutoTopupAmount, theWallet.rAutoTopupAmount, theWallet.autoTopupAmount, getTopupTargetDefault()); 

      if (val == null || val < getTopupTargetMinimum() || val > getTopupTargetLimit()) {
        return false;
      }
  
      // Don't allow the amout to be less than the threshold
      let rAutoTopupThreshold = this.getTopupValue(this.state.rAutoTopupThreshold, theWallet.rAutoTopupThreshold, theWallet.autoTopupThreshold, getTopupThresholdDefault()); 
      if (rAutoTopupThreshold == null || val < rAutoTopupThreshold) {
        return false;
      }
    } else {
      let val = this.getTopupValue(this.state.rAutoTopupAmount, theAccount.rAutoTopupAmount, theAccount.autoTopupAmount, getTopupTargetDefault());
      if (val == null || val <  getTopupTargetMinimum() || val > getTopupTargetLimit()) {
        return false;
      }

      // Don't allow the amout to be less than the threshold
      let rAutoTopupThreshold = this.getTopupValue(this.state.rAutoTopupThreshold, theAccount.rAutoTopupThreshold, theAccount.autoTopupThreshhold, getTopupThresholdDefault()); 
      if (rAutoTopupThreshold == null || val < rAutoTopupThreshold) {
        return false;
      }      
    }

    return true;
  }

  getAmountErrorMessage(theAccount, theWallet) {
    const { getTopupTargetDefault, getTopupThresholdDefault } = this.props.auth;

    let errMesg = null;

    // If  wallet is set, we're doing project funding.  Otherwise, account funding.
    if (theWallet != null) {
      let amountVal    = this.getTopupValue(this.state.rAutoTopupAmount, theWallet.rAutoTopupAmount, theWallet.autoTopupAmount, getTopupTargetDefault()); 
      let thresholdVal = this.getTopupValue(this.state.rAutoTopupThreshold, theWallet.rAutoTopupThreshold, theWallet.autoTopupThreshold, getTopupThresholdDefault()); 
  
      if (amountVal < thresholdVal) {
        errMesg = 'Cannot be less than falls below value';
      }
    } else {
      let amountVal    = this.getTopupValue(this.state.rAutoTopupAmount, theAccount.rAutoTopupAmount, theAccount.autoTopupAmount, getTopupTargetDefault()); 
      let thresholdVal = this.getTopupValue(this.state.rAutoTopupThreshold, theAccount.rAutoTopupThreshold, theAccount.autoTopupThreshhold, getTopupThresholdDefault()); 

      if (amountVal < thresholdVal) {
        errMesg = 'Cannot be less than falls below value';
      }
    }
    return errMesg;
  }

  isAmountError(theAccount, theWallet) {
    if (this.getAmountErrorMessage(theAccount, theWallet) == null) {
      return false;
    }
    return true;
  }

  isMaxMonthlyBudgetValid(theAccount, theWallet) {
    const { getMaxMonthlyBudgetLimit, getMaxMonthlyBudgetDefault, getMaxMonthlyBudgetMinimum } = this.props.auth;

    // If  wallet is set, we're doing project funding.  Otherwise, account funding.
    if (theWallet != null) {
      // Need to accommodate null as a valid value as well. If the value has been set and is null, that is valid.
      if (this.state.rMaxMonthlyBudgetSet === true && this.state.rMaxMonthlyBudget == null) {
        return true;
      }
      // If the value has not been set, and the original is null, that is valid.
      if (this.state.rMaxMonthlyBudgetSet === false && theWallet.rMaxMonthlyBudget == null) {
        return true;
      }

      // Falling through to here means that we'll use actual values
      let val = getNumericValueForRender(this.state.rMaxMonthlyBudget, theWallet.rMaxMonthlyBudget, getMaxMonthlyBudgetDefault()); 
      if (val < getMaxMonthlyBudgetMinimum() || val > getMaxMonthlyBudgetLimit()) {
          return false;
      }
    } else {
      // Need to accommodate null as a valid value as well. If the value has been set and is null, that is valid.
      if (this.state.rMaxMonthlyBudgetSet === true && this.state.rMaxMonthlyBudget == null) {
        return true;
      }
      // If the value has not been set, and the original is null, that is valid.
      if (this.state.rMaxMonthlyBudgetSet === false && theAccount.rMaxMonthlyBudget == null) {
        return true;
      }

      // Falling through to here means that we'll use actual values
      let val = getNumericValueForRender(this.state.rMaxMonthlyBudget, theAccount.rMaxMonthlyBudget, getMaxMonthlyBudgetDefault()); 
      if (val < getMaxMonthlyBudgetMinimum() || val > getMaxMonthlyBudgetLimit()) {
          return false;
      }
    }

    return true;
  }

  // Determine if budget save should be disabled
  isNextDisabled(theAccount, theProject, theWallet, customerSubscriptionCount, autoTopupActive) { 
    if (customerSubscriptionCount > 0 || theAccount.rPreApproveSubs === 1 || theAccount.preApproveSubs === 1) {
      // Check the immediate topup amounts
      if (! this.isInitialBalanceAmountValid(theAccount, theProject, theWallet)) {
        return true;
      }

      // Auto-topup amounts only need to be checked if auto-topup is active
      if (autoTopupActive === 1) {
        if (! this.isAmountValid(theAccount, theWallet)) {
          return true;
        }
        if (! this.isThresholdValid(theAccount, theProject, theWallet)) {
          return true;
        }
      }

      // Check the max monthly budget
      if (!this.isMaxMonthlyBudgetValid(theAccount, theWallet)) {
        return true;
      }
    }

    return false;
  }

  resetAutoTopupThreshold(theAccount, theWallet) {
    let resetValueTo = theAccount.autoTopupThreshhold;
    if (theWallet != null) {
      resetValueTo = theWallet.autoTopupThreshold;
    }
    this.setState({rAutoTopupThreshold: resetValueTo});
  }
  
  resetAutoTopupAmount(theAccount, theWallet) {
    let resetValueTo = theAccount.autoTopupAmount;
    if (theWallet != null) {
      resetValueTo = theWallet.autoTopupAmount;
    }
    this.setState({rAutoTopupAmount: resetValueTo});
  }

  resetMaxMonthlyBudget(theAccount, theWallet) {
    let resetValueTo = theAccount.maxMonthlyBudget;
    if (theWallet != null) {
      resetValueTo = theWallet.maxMonthlyBudget;
    }
    this.setState({rMaxMonthlyBudget: resetValueTo, rMaxMonthlyBudgetSet: true});
  }

  goToMyFunding(url, tab) {
    localStorage.setItem('psInOut', tab);
    this.props.history.push(`${url}`);
  }

  updateBudget(theAccount, theProject, theWallet, customerSubscriptionCount, nextStep) {
    const { getInitialBalanceAmountDefault, getTopupTargetDefault, getTopupThresholdDefault } = this.props.auth;
    this.setState({ showSpinner: 'true' });

    const { initialBalanceAmount, oneTimeFeeRequestSet, rAutoTopupActive, rAutoTopupAmount, rAutoTopupThreshold, rMaxCharge, rMaxMonthlyBudget, rMaxMonthlyBudgetSet } = this.state;

    let forceFetch = this.state.forceFetch;
    forceFetch++;

    // If project and wallet are set, we're doing project funding.  Otherwise, account funding.
    if (theProject != null && theWallet != null) {
      let didBudgetChange = ((this.hasValueChanged(rAutoTopupActive, theWallet.rAutoTopupActive) ||
                              this.hasNumericValueChanged(rAutoTopupAmount,     theWallet.rAutoTopupAmount) ||
                              this.hasNumericValueChanged(rAutoTopupThreshold,  theWallet.rAutoTopupThreshold) ||
                              this.hasNumericValueChanged(rMaxMonthlyBudget,    theWallet.rMaxMonthlyBudget) ||
                              this.hasNumericValueChanged(initialBalanceAmount, theWallet.initialBalanceAmount) ||
                              //these last two are here because these values could have already changed and user coming back
                              //or they changed on funding page and they were directed here
                              this.hasNumericValueChanged(rAutoTopupAmount,    theWallet.autoTopupAmount) ||
                              this.hasNumericValueChanged(rAutoTopupThreshold, theWallet.autoTopupThreshold) ));

      if (didBudgetChange === false) {
        //this is here because we want to record an initial budget change EVEN if they did not change a value but left the defaults.
        //the way we know this is because the account did not have a previous value in these two fields.
        if ((Number(theWallet.autoTopupAmount) === 0) || (Number(theWallet.autoTopupThreshold) === 0)) {
          didBudgetChange = true;
        }
      }

      if (customerSubscriptionCount > 0 || theProject.rPreApproveSubs === 1 || theProject.preApproveSubs === 1) {
        //maintenance fields visible, lets update them
        let autoTopupActive = getValueForRender(rAutoTopupActive, theWallet.rAutoTopupActive, theWallet.autoTopupActive, 1);

        RequestedBudgetWalletUpdateMutation.commit(theWallet.walletId, 
          ((theProject.budgetApprovedCount === 0) ? getValueForRender(initialBalanceAmount, theWallet.initialBalanceAmount, getInitialBalanceAmountDefault()) : null), //initial balance is only set first time
          autoTopupActive,
          this.getTopupValue(rAutoTopupAmount, theWallet.rAutoTopupAmount, theWallet.autoTopupAmount, getTopupTargetDefault()), 
          this.getTopupValue(rAutoTopupThreshold, theWallet.rAutoTopupThreshold, theWallet.autoTopupThreshold, getTopupThresholdDefault()), 
          rMaxCharge, rMaxMonthlyBudget, rMaxMonthlyBudgetSet,
          initialBalanceAmount, oneTimeFeeRequestSet)
        .then(response => {
          RequestedBudgetProjectUpdateMutation.commit(theProject.projectId, 0)
          }).then(response => {
          this.setState({ forceFetch: forceFetch, showSpinner: false});
          if (nextStep === 'next') {
            this.props.handleNext(didBudgetChange);
          } else {
            this.props.handleBack();
          }
        })
        .catch(error => {
          this.setState({ forceFetch: forceFetch, showSpinner: false});
        });
      } else {
        // Unset the requested budget information.  This only happens if there are no customer pay subscriptions, and
        // pre-approval is not on, and this is undoing a previous budget being turned on in the SO, then stepping
        // back.  Once the customer has accepted a customer pay subscription or a pre-approval, the budget cannot be
        // undone, so this is quite an edge case.
        RequestedBudgetWalletUnsetMutation.commit(theWallet.walletId)
        .then(response => {
          return RequestedBudgetProjectUpdateMutation.commit(theProject.projectId, 0);
        })
        .then(response => {
          this.setState({ forceFetch: forceFetch, showSpinner: false});
          if (nextStep === 'next') {
            this.props.handleNext(false);
          } else {
            this.props.handleBack();
          }
        })
        .catch(error => {
          this.setState({ forceFetch: forceFetch, showSpinner: false});
        });
      }
    } else {
      let accountId = this.props.customer.accountId;

      let didBudgetChange = ((this.hasValueChanged(rAutoTopupActive, theAccount.rAutoTopupActive) ||
                              this.hasNumericValueChanged(rAutoTopupAmount, theAccount.rAutoTopupAmount) ||
                              this.hasNumericValueChanged(rAutoTopupThreshold,  theAccount.rAutoTopupThreshold) ||
                              this.hasNumericValueChanged(rMaxMonthlyBudget, theAccount.rMaxMonthlyBudget) ||
                              this.hasNumericValueChanged(initialBalanceAmount, theAccount.initialBalanceAmount) ||
                              //these last two are here because these values could have already changed and user coming back
                              //or they changed on funding page and they were directed here
                              this.hasNumericValueChanged(theAccount.rAutoTopupAmount, theAccount.autoTopupAmount) ||
                              this.hasNumericValueChanged(theAccount.rAutoTopupThreshold, theAccount.autoTopupThreshhold) ));

      if (didBudgetChange === false) {
        //this is here because we want to record an initial budget change EVEN if they did not change a value but left the defaults.
        //the way we know this is because the account did not have a previous value in these two fields.
        if ((Number(theAccount.autoTopupAmount) === 0) || (Number(theAccount.autoTopupThreshhold) === 0)) {
          didBudgetChange = true;
        }
      }

      if (customerSubscriptionCount > 0 || theAccount.rPreApproveSubs === 1 || theAccount.preApproveSubs === 1) {
        //maintenance fields visible, lets update them
        let autoTopupActive = getValueForRender(rAutoTopupActive, theAccount.rAutoTopupActive, theAccount.autoTopupActive, 1);
        RequestedBudgetAccountUpdateMutation.commit(accountId, 1, 1, 
          ((theAccount.budgetApprovedCount === 0) ? getValueForRender(initialBalanceAmount, theAccount.initialBalanceAmount, getInitialBalanceAmountDefault()) : null), //initial balance is only set first time 
          autoTopupActive,
          this.getTopupValue(rAutoTopupAmount, theAccount.rAutoTopupAmount, theAccount.autoTopupAmount, getTopupTargetDefault()), 
          this.getTopupValue(rAutoTopupThreshold, theAccount.rAutoTopupThreshold, theAccount.autoTopupThreshhold, getTopupThresholdDefault()), 
          rMaxCharge, rMaxMonthlyBudget, rMaxMonthlyBudgetSet,
          initialBalanceAmount, oneTimeFeeRequestSet)
        .then(response => {
          this.setState({ forceFetch: forceFetch, showSpinner: false});
          if (nextStep === 'next') {
            this.props.handleNext(didBudgetChange);
          } else {
            this.props.handleBack();
          }
        })
        .catch(error => {
          this.setState({ forceFetch: forceFetch, showSpinner: false});
        });
      } else {
        // Unset the requested budget information.  This only happens if there are no customer pay subscriptions, and
        // pre-approval is not on, and this is undoing a previous budget being turned on in the SO, then stepping
        // back.  Once the customer has accepted a customer pay subscription or a pre-approval, the budget cannot be
        // undone, so this is quite an edge case.
        RequestedBudgetAccountUnsetMutation.commit(accountId)
        .then(response => {
          this.setState({ forceFetch: forceFetch, showSpinner: false});
          if (nextStep === 'next') {
            this.props.handleNext(false);
          } else {
            this.props.handleBack();
          }
        })
        .catch(error => {
          this.setState({ forceFetch: forceFetch, showSpinner: false});
        });
      }
    }
  }

  showSpinner() {
    return this.state.showSpinner;
  }

  toggleAutoTopup(ata, autoTopupAmount, autoTopupThreshold) {
    const { getTopupTargetMinimum, getTopupTargetDefault, getTopupTargetLimit, getTopupThresholdMinimum, getTopupThresholdDefault, getTopupThresholdLimit } = this.props.auth;

    if (ata === 1) {
      // Turn on auto-topup.  If there are values for topup amount and threshold, reset to these.
      let origAmount = this.state.origRAutoTopupAmount;
      let origThreshold = this.state.origRAutoTopupThreshold;

      // Look at currently active values (if available)
      if (origAmount == null || origAmount < getTopupTargetMinimum() || origAmount > getTopupTargetLimit()) {
        origAmount = autoTopupAmount;
      }
      if (origThreshold == null || origThreshold < getTopupThresholdMinimum() || origThreshold > getTopupThresholdLimit()) {
        origThreshold = autoTopupThreshold;
      }

      // Fall back to defaults
      if (origAmount == null || origAmount < getTopupTargetMinimum() || origAmount > getTopupTargetLimit()) {
        origAmount = getTopupTargetDefault();
      }
      if (origThreshold == null || origThreshold < getTopupThresholdMinimum() || origThreshold > getTopupThresholdLimit()) {
        origThreshold = getTopupThresholdDefault();
      }

      this.setState({rAutoTopupActive: 1, rAutoTopupAmount: origAmount, rAutoTopupThreshold: origThreshold});
    } else {
      // Turn off auto-topup
      let origAmount = this.state.rAutoTopupAmount;
      let origThreshold = this.state.rAutoTopupThreshold;

      // Look at currently active values (if available)
      if (origAmount == null || origAmount < getTopupTargetMinimum() || origAmount > getTopupTargetLimit()) {
        origAmount = autoTopupAmount;
      }
      if (origThreshold == null || origThreshold < getTopupThresholdMinimum() || origThreshold > getTopupThresholdLimit()) {
        origThreshold = autoTopupThreshold;
      }
      
      // Fall back to defaults
      if (origAmount == null || origAmount < getTopupTargetMinimum() || origAmount > getTopupTargetLimit()) {
        origAmount = getTopupTargetDefault();
      }
      if (origThreshold == null || origThreshold < getTopupThresholdMinimum() || origThreshold > getTopupThresholdLimit()) {
        origThreshold = getTopupThresholdDefault();
      }

      this.setState({rAutoTopupActive: 0, rAutoTopupAmount: 0, rAutoTopupThreshold: 0, origRAutoTopupAmount: origAmount, origRAutoTopupThreshold: origThreshold});
    }
  }

  toggleSetMonthlyBudget(useMonthlyBudget, origBudget, savedMonthlyBudget) {
    const { getMaxMonthlyBudgetDefault } = this.props.auth;
    if (useMonthlyBudget === false) {
      let resetValueTo = origBudget;
      
      if (resetValueTo == null) {
        resetValueTo = savedMonthlyBudget;
      }

      if (resetValueTo == null) {
        resetValueTo = getMaxMonthlyBudgetDefault();
      }

      this.setState({rMaxMonthlyBudget: resetValueTo, rMaxMonthlyBudgetSet: true});
    } else {
      // Turn off the budget
      this.setState({rMaxMonthlyBudget: null, rMaxMonthlyBudgetSet: true});
    }
  }

  toggleSetOneTimeFeeRequest(theAccount, theWallet, useOneTimeFeeRequest, origOneTimeFeeRequest) {
    const { getInitialBalanceAmountDefault, getOneTimeBalanceAmountMinimum } = this.props.auth;
    let toggle = !useOneTimeFeeRequest;
    if (toggle === true) {
      // Turn on a one time request
      if (origOneTimeFeeRequest != null) {
        this.setState({initialBalanceAmount: origOneTimeFeeRequest, oneTimeFeeRequestSet: true});
      } else {
        // Use a default of auto-topup.  The point of this is that we want to use an amount
        // that makes sense if there is an auto-topup amount set.  However, if there isn't, fall back
        // to the default for the initial balance amount.
        let defaultOneTime = getNumericValueForRender(this.state.rAutoTopupAmount, theAccount.rAutoTopupAmount, theAccount.autoTopupAmount, getInitialBalanceAmountDefault());
        if (theWallet != null) {
          defaultOneTime = getNumericValueForRender(this.state.rAutoTopupAmount, theWallet.rAutoTopupAmount, theWallet.autoTopupAmount, getInitialBalanceAmountDefault());
        }

        if (defaultOneTime < getOneTimeBalanceAmountMinimum()) {
          defaultOneTime = getInitialBalanceAmountDefault();
        }
        this.setState({initialBalanceAmount: defaultOneTime, oneTimeFeeRequestSet: true});
      }
    } else {
      // Turn off the  one time request
      this.setState({initialBalanceAmount: null, oneTimeFeeRequestSet: true});
    }
  }

  setOneTimeFeeRequest(theAmount) {
    this.setState({initialBalanceAmount: theAmount, oneTimeFeeRequestSet: true});
  }

  render() {
    const { logError, getMaxMonthlyBudgetLimit, getMaxMonthlyBudgetMinimum, getInitialBalanceAmountMinimum, getInitialBalanceAmountDefault, getInitialBalanceAmountLimit, getOneTimeBalanceAmountMinimum, getTopupActiveDefault, getTopupThresholdMinimum, getTopupThresholdDefault, getTopupThresholdLimit, getTopupTargetMinimum, getTopupTargetDefault, getTopupTargetLimit } = this.props.auth;

    const classes = this.props.classes;

    let displayOnly = false;
    if (this.props.activeStep > this.props.assignedStep) {
      displayOnly = true;
    }

    let acctId = this.props.customer.accountId;

    // Was a project passed in?  If so, the subscription is for a project.
    // If not, legacy case.
    let aProject = this.props.project;
    let projectId = null;
    if (aProject != null && aProject.projectId != null) {
      projectId = aProject.projectId;
    }

    return (
      <QueryRenderer
        environment={abEnvironment.getEnvironment()}
        query={StepBudgetCustomerQuery}
        variables={{accountId: acctId, projectId: projectId, forceFetch: this.state.forceFetch}}
        render={({error, props})=> {
          if (error) {
            logError(error.message);
            return (
              <ServerError/>
            );
          } else if (props) {

            let theAccount     = props.accountTByAccountId;
            let theAccountName = theAccount.accountName;

            //
            // If projects are used, values will be on the project and wallet.
            // If projects are not used, values will be on the account.
            // - so set up variables that will be used on the UI here, and set
            //   them from either the project/wallet or the account.
            // 

            // Project if relevant
            let theProject = null;
            let theWallet = null;
            if (theAccount.projectTsByCustomerAccountId.totalCount > 0) {
              theProject = theAccount.projectTsByCustomerAccountId.nodes[0];
              theWallet  = theProject.walletTByCustomerWalletId;
            }

            let autoTopupActive      = theAccount.autoTopupActive;
            let autoTopupAmount      = theAccount.autoTopupAmount;      // Current auto-topup amount
            let autoTopupThreshold   = theAccount.autoTopupThreshhold;  // Current auto-topup threshold
            let maxMonthlyBudget     = theAccount.maxMonthlyBudget;     // Current max monthly budget
            let initialBalanceAmount = theAccount.initialBalanceAmount; // New initial balance amount
            let rAutoTopupActive     = theAccount.rAutoTopupActive;     // New auto-topup setting
            let rAutoTopupAmount     = theAccount.rAutoTopupAmount;     // New auto-topup amount
            let rAutoTopupThreshold  = theAccount.rAutoTopupThreshold;  // New auto-topup threshold

            if (theWallet != null) {
              autoTopupActive      = theWallet.autoTopupActive;
              autoTopupAmount      = theWallet.autoTopupAmount;
              autoTopupThreshold   = theWallet.autoTopupThreshold;
              maxMonthlyBudget     = theWallet.maxMonthlyBudget;
              initialBalanceAmount = theWallet.initialBalanceAmount;
              rAutoTopupActive     = theWallet.rAutoTopupActive;
              rAutoTopupAmount     = theWallet.rAutoTopupAmount;
              rAutoTopupThreshold  = theWallet.rAutoTopupThreshold;
            }

            // autoTopupActive is the value that will be used to set a switch for the UI.  If there is a state value, we'll
            // use that, as that indicates that the user has changed the value.  Otherwise, use an rValue if it exists, and if
            // not, use the current value, and finally fall back to feature flag default.
            autoTopupActive = getValueForRender(this.state.rAutoTopupActive, rAutoTopupActive, autoTopupActive, getTopupActiveDefault());

            // max monthly budget can be null, which indicates that there is no limit, or can be a numeric value
            // So rMaxMonthlyBudget is what will be used to determine what to display.
            // - null means there is no limit
            // - non-null is the limit value
            let origrMaxMonthlyBudget = theAccount.rMaxMonthlyBudget;
            if (theWallet != null) {
              origrMaxMonthlyBudget = theWallet.rMaxMonthlyBudget;
            }

            let rMaxMonthlyBudget = null;
            if (this.state.rMaxMonthlyBudgetSet === true) {
              // Use the state val
              rMaxMonthlyBudget = this.state.rMaxMonthlyBudget;
            } else {
              rMaxMonthlyBudget = origrMaxMonthlyBudget;
            }
            let useMonthlyBudget = false;
            if (rMaxMonthlyBudget != null) {
              useMonthlyBudget = true;
            }

            // initial balance can be null, which indicates that there is no one time fee, or can be a numeric value
            // - null means there is no one time fee
            // - non-null means there is a fee
            let origOneTimeFeeRequest = initialBalanceAmount;

            let oneTimeFeeRequest = null;
            if (this.state.oneTimeFeeRequestSet === true) {
              // Use the state val
              oneTimeFeeRequest = this.state.initialBalanceAmount;
            } else {
              oneTimeFeeRequest = origOneTimeFeeRequest;
            }
            let useOneTimeFeeRequest = false;
            if (oneTimeFeeRequest != null) {
              useOneTimeFeeRequest = true;
            }

            // These are the values for display in the text fields.  This is complex, as we want to:
            // - display the state value if non-null.
            // - then use the rValue on the db if non-null.
            // - then use the current value if non-null.
            // - then use the default value.
            //
            let rAutoTopupAmountValue    = this.getTopupValue(this.state.rAutoTopupAmount, rAutoTopupAmount, autoTopupAmount, getTopupTargetDefault());
            let rAutoTopupThresholdValue = this.getTopupValue(this.state.rAutoTopupThreshold, rAutoTopupThreshold, autoTopupThreshold, getTopupThresholdDefault());

            let budgetValuesExist = false;
            let autoTopupAmountLabel = '';
            let autoTopupThresholdLabel = '';
            let maxMonthlyBudgetLabel = '';
            let autoTopupAmountDisplayOnlyLabel = '';
            let autoTopupThresholdDisplayOnlyLabel = '';
            let maxMonthlyBudgetDisplayOnlyLabel = '';

            // Derive what the current r values should be, and if they are different from the current values, set a reset label
            if (autoTopupActive === 1 && autoTopupThreshold != null && autoTopupThreshold > 0) {
              budgetValuesExist = true;
              if (Number(autoTopupAmount) === Number(rAutoTopupAmountValue)) {
                  autoTopupAmountLabel = '';
              } else {
                autoTopupAmountLabel = 'Reset to current active value (' + Number(autoTopupAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}) + ')';
                autoTopupAmountDisplayOnlyLabel = 'Changed from current active value (' + Number(autoTopupAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}) + ')';
              }
              if (Number(autoTopupThreshold) === Number(rAutoTopupThresholdValue)) {
                  autoTopupThresholdLabel = '';
              } else {
                autoTopupThresholdLabel = 'Reset to current active value (' + Number(autoTopupThreshold).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}) + ')';
                autoTopupThresholdDisplayOnlyLabel = 'Changed from current active value (' + Number(autoTopupThreshold).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}) + ')';
              }
            }

            if (maxMonthlyBudget != null) {
              if (Number(maxMonthlyBudget) === Number(rMaxMonthlyBudget)) {
                maxMonthlyBudgetLabel = '';
              } else {
                maxMonthlyBudgetLabel = 'Reset to current active value (' + Number(maxMonthlyBudget).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}) + ')';
                maxMonthlyBudgetDisplayOnlyLabel = 'Changed from current active value (' + Number(maxMonthlyBudget).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}) + ')';
              }
            } else {
              if (rMaxMonthlyBudget == null) {
                maxMonthlyBudgetLabel = '';
              } else {
                maxMonthlyBudgetLabel = 'Reset to current active setting (no limit)';
                maxMonthlyBudgetDisplayOnlyLabel = 'Changed from current active setting (no limit)';
              }
            }

            let customerSubscriptions = [];
            let existingCustSubCount = 0;
            let pendingCustSubCount = 0;

            for (let i = 0; i < theAccount.customerAppTsByCustomerAccountId.nodes.length; i++) {
              let sub = theAccount.customerAppTsByCustomerAccountId.nodes[i];
              // exclude cancelled subs and auth only subs
              if (sub.appStatus !== 2 && sub.bindCardTByBcId.bcStatus !== 4) {
                if (sub.rPaymentOrder !== null) {
                  if (sub.rPaymentOrder !== 2) {
                    customerSubscriptions.push(sub);
                    if (sub.bindCardTByBcId.bcStatus === 0) {
                      existingCustSubCount++;
                    } else {
                      pendingCustSubCount++;
                    }
                  } 
                } else {
                  if (sub.paymentOrder !== 2) {
                    customerSubscriptions.push(sub);
                    if (sub.bindCardTByBcId.bcStatus === 0) {
                      existingCustSubCount++;
                    } else {
                      pendingCustSubCount++;
                    }
                  } 
                }
              }
            }

            // sort customer subscription list
            if (customerSubscriptions.length > 0) {
              customerSubscriptions.sort(function(a, b) {
                if(a.appTByAppId.appName < b.appTByAppId.appName) { return -1; }
                if(a.appTByAppId.appName > b.appTByAppId.appName) { return 1; }
                return 0;
              });
            }

            let customerAccordionTitle1 = 'Set up an expense wallet for ' + theAccountName + ' to fund subscription costs for';
            let customerAccordionTitle1a = 'Set up an expense wallet for ' + theAccountName + ' to fund subscription costs';
            let customerAccordionTitleDo1 = 'Expense wallet for ' + theAccountName + ' to fund subscription costs for ';
            let customerAccordionTitle2 = '';
            if (existingCustSubCount === 1) {
              customerAccordionTitle2 = existingCustSubCount + ' existing subscription';
            } else if (existingCustSubCount > 1) {
              customerAccordionTitle2 = existingCustSubCount + ' existing subscriptions';
            }

            if (pendingCustSubCount === 1) {
              if (customerAccordionTitle2 === '') {
                customerAccordionTitle2 = pendingCustSubCount + ' new subscription';
              } else {
                customerAccordionTitle2 = customerAccordionTitle2 + ' and ' + pendingCustSubCount + ' new subscription';
              }
            } else if (pendingCustSubCount > 1) {
              if (customerAccordionTitle2 === '') {
                customerAccordionTitle2 = pendingCustSubCount + ' new subscriptions';
              } else {
                customerAccordionTitle2 = customerAccordionTitle2 + ' and ' + pendingCustSubCount + ' new subscriptions';
              }
            }

            // get the cached available balance value
            // TODO: we don't do anything with this right now, and it isn't correct.  But we should be
            //       able to show a current balance eventually, so leaving this comment in as a placeholder. 
            /*
            let availableBalance = abEnvironment.getUserAttribute('a_availableBalance');
            if (availableBalance == null || availableBalance === '') {
              availableBalance = '$0.00';
            }
            */

            let budgetApprovedCount = theAccount.budgetApprovedCount;
            if (theProject != null) {
              budgetApprovedCount = theProject.budgetApprovedCount;
            }

            if (!displayOnly) {
              return (
                <Grid container spacing={2} >
                  {(customerSubscriptions.length > 0 || theAccount.rPreApproveSubs === 1 || theAccount.preApproveSubs === 1) &&
                  <Grid item xs={12}>      
                    <Paper className={classes.paper}>
                      <Grid container spacing={2} direction="row" justifyContent="space-between" >
                        <Grid item xs={12}>
                          <Typography variant="h6">
                            Wallet for subscriptions expensed to {theAccountName}
                          </Typography>
                          {/* Edge case - no subscriptions */}
                          {customerSubscriptions.length === 0 &&
                          <Typography>{customerAccordionTitle1a}</Typography>}
                        </Grid>
                        
                        {customerSubscriptions.length > 0 &&
                        <Grid item xs={12}>
                          <Accordion>
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              aria-controls="panel1a-content"
                              id="panel1a-header"
                            >
                              <Typography>
                                {customerAccordionTitle1}&nbsp;
                                <span style={{color: "blue", textDecoration: "underline"}}>{customerAccordionTitle2}</span>
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              {customerSubscriptions.length > 0 &&
                              <TableContainer>
                                <StyledTable>
                                  <TableHead>
                                    <TableRow>
                                      <StyledTableCell sx={{ borderTopLeftRadius: 8}}/>
                                      <StyledTableCell>Subscription</StyledTableCell>
                                      <StyledTableCell>Terms</StyledTableCell>
                                      <StyledTableCell sx={{ borderTopRightRadius: 8}}/>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {customerSubscriptions.map(n => {
                                      return (
                                        <SubscriptionTableRow 
                                          key={n.customerAppId}
                                          subscription={n}
                                          styledTableRow={StyledTableRow}
                                          styledTableCell={StyledTableCell}
                                          editable={false}
                                          showModel={false}
                                        />
                                      );
                                    })}
                                  </TableBody>
                                </StyledTable>
                              </TableContainer>}
                            </AccordionDetails>
                          </Accordion>
                        </Grid>}

                        <Grid item xs={12}><Divider style={{marginTop:8}}/></Grid>
                        <Grid item xs={12}>
                          <Grid container spacing={1} direction="row" justifyContent="space-between" >
                            <Grid item xs={12}>
                              <Typography variant="h6">
                                Funding
                              </Typography>
                            </Grid>

                            {/* Only show initially fund on first time use - that is, until a budget has been accepted */}
                            {budgetApprovedCount === 0 &&
                            <Grid item xs={12}>
                              <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                                <Grid item>
                                  <Grid container justifyContent="flex-start" alignItems="center" spacing={0}>
                                    <Grid item>
                                      <Grid container direction="column" spacing={0}>
                                        <Grid item>
                                          <Typography variant="body1" style={AppBindStyles.boldLabel}>Initially fund balance</Typography>
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                    <Grid>
                                      <Tooltip placement='bottom' title="Your customer will be billed this amount to fund their balance when they accept this order.">
                                        <HelpOutline fontSize="small" style={{verticalAlign: "-7%", marginLeft: '4px'}}/>
                                      </Tooltip>
                                    </Grid>
                                  </Grid>
                                </Grid>
                                <Grid item > 
                                  <CurrencyTextField id="initialBalanceAmount" className={classes.currencyField3}
                                    value={getValueForRender(this.state.initialBalanceAmount, initialBalanceAmount, getInitialBalanceAmountDefault() )}
                                    showErrMsg={true}
                                    allowNegative={false}
                                    minValue={getInitialBalanceAmountMinimum()}
                                    maxValue={getInitialBalanceAmountLimit()}
                                    minEnterValue={0}
                                    maxEnterValue={999999}
                                    onChange={(e) => this.setOneTimeFeeRequest(e.target.value) }
                                    styleSheet={styleSheet}
                                    textAlign="right"
                                  />
                                </Grid>
                              </Grid>
                            </Grid>}
                            
                            {budgetApprovedCount > 0 &&
                            <Grid item xs={12}>
                              <Grid container spacing={0} direction="row" >
                                <Grid item xs={12}>
                                  <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                                    <Grid item>
                                      <Grid container justifyContent="flex-start" alignItems="center" spacing={0}>
                                        <Grid item>
                                          <FormControlLabel
                                              control={
                                                <Switch
                                                  checked={useOneTimeFeeRequest === true}
                                                  onChange={(e) => this.toggleSetOneTimeFeeRequest(theAccount, theWallet, useOneTimeFeeRequest, origOneTimeFeeRequest)}
                                                  value={useOneTimeFeeRequest}
                                                  color="primary"
                                                />
                                              }
                                              label={<Typography variant="body1" style={AppBindStyles.boldLabel}>Add funds now</Typography>}
                                          />
                                        </Grid>
                                        <Grid item>
                                          <Tooltip placement='bottom' title="Your customer will be billed this amount to fund their balance when they accept this order.">
                                            <HelpOutline fontSize="small" style={{verticalAlign: "-7%", marginLeft: '-12px'}}/>
                                          </Tooltip>
                                        </Grid>
                                      </Grid>
                                    </Grid>

                                    {oneTimeFeeRequest != null &&
                                    <Grid item>
                                      <CurrencyTextField id="oneTimeFeeRequestPending" className={classes.currencyField3}
                                        value={oneTimeFeeRequest}
                                        showErrMsg={true}
                                        allowNegative={false}
                                        minValue={getOneTimeBalanceAmountMinimum()}
                                        maxValue={getInitialBalanceAmountLimit()}
                                        minEnterValue={0}
                                        maxEnterValue={99999999}
                                        onChange={(e) => this.setOneTimeFeeRequest(e.target.value) }
                                        textAlign="right"
                                      />
                                    </Grid>}
                                    {oneTimeFeeRequest == null &&
                                    <Typography className={classes.noAddFundsHolder} variant="body1"><i>No request</i></Typography>}
                                  </Grid>
                                </Grid>                           
                              </Grid>
                            </Grid>}
                                    
                            <Grid item xs={12}>
                              <Grid container justifyContent="flex-start" alignItems="center" spacing={0}>
                                <Grid item>
                                  <FormControlLabel
                                    control={
                                      <Switch 
                                        checked={autoTopupActive === 1} 
                                        onChange={(e, checked) => {
                                          if (checked) {
                                            this.toggleAutoTopup(1, autoTopupAmount, autoTopupThreshold);
                                          } else {
                                            this.toggleAutoTopup(0, autoTopupAmount, autoTopupThreshold);
                                          }
                                        }}
                                        color="primary"
                                      />
                                    }
                                    label={<Typography variant="body1" style={AppBindStyles.boldLabel}>Automatically add funds</Typography>}
                                  />
                                </Grid>
                                <Grid item>
                                  <Tooltip placement='bottom' title="When your customer's balance goes below the limit specified, they'll be billed the amount needed to bring their balance back up to the desired funding level.">
                                    <HelpOutline fontSize="small" style={{verticalAlign: "-7%", marginLeft: '-12px'}}/>
                                  </Tooltip>
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid item xs={12}>
                              <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                                <Grid item>
                                  {autoTopupActive === 0 &&
                                  <Typography variant="body1">When balance falls below</Typography>}
                                  {autoTopupActive !== 0 &&
                                  <Typography variant="body1" style={AppBindStyles.boldLabel}>When balance falls below</Typography>}
                                </Grid>
                                
                                <Grid item >
                                  {budgetValuesExist &&
                                  <Grid container spacing={1} alignItems="center">
                                    <Grid item>
                                      {autoTopupThresholdLabel !== '' &&
                                      <Tooltip placement='bottom' title={autoTopupThresholdLabel}>
                                        <SettingsBackupRestore 
                                         fontSize="small" 
                                         style={{verticalAlign: "-10%"}}
                                         onClick={() => { this.resetAutoTopupThreshold(theAccount, theWallet);}}
                                        />
                                      </Tooltip>}
                                    </Grid>
                                    <Grid item>
                                      <CurrencyTextField id="fallsBelowPending" className={classes.currencyField3}
                                        value={rAutoTopupThresholdValue}
                                        showErrMsg={true}
                                        error={this.isThresholdError(theAccount, theProject, theWallet)}
                                        errorMsg={this.getThresholdErrorMessage(theAccount, theProject, theWallet)}
                                        allowNegative={false}
                                        minValue={getTopupThresholdMinimum()}
                                        maxValue={getTopupThresholdLimit()}
                                        minEnterValue={0}
                                        maxEnterValue={999999}
                                        onChange={(e) => this.setState({ rAutoTopupThreshold: e.target.value })}
                                        styleSheet={styleSheet}
                                        textAlign="right"
                                      />
                                    </Grid>
                                  </Grid>}
                                  {! budgetValuesExist &&
                                  <CurrencyTextField id="fallsBelowPending" className={classes.currencyField3}
                                    value={rAutoTopupThresholdValue}
                                    showErrMsg={true}
                                    allowNegative={false}
                                    error={this.isThresholdError(theAccount, theProject, theWallet)}
                                    errorMsg={this.getThresholdErrorMessage(theAccount, theProject, theWallet)}
                                    minValue={getTopupThresholdMinimum()}
                                    maxValue={getTopupThresholdLimit()}
                                    minEnterValue={0}
                                    maxEnterValue={999999}
                                    onChange={(e) => this.setState({ rAutoTopupThreshold: e.target.value })}
                                    styleSheet={styleSheet}
                                    disabled={autoTopupActive === 0}
                                    textAlign="right"
                                  />}
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid item xs={12}>
                              <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                                <Grid item>
                                  {autoTopupActive === 0 &&
                                  <Typography variant="body1">Fund balance to</Typography>}
                                  {autoTopupActive !== 0 &&
                                  <Typography variant="body1" style={AppBindStyles.boldLabel}>Fund balance to</Typography>}
                                </Grid>
                                <Grid item > 
                                  {budgetValuesExist &&
                                  <Grid container spacing={1} alignItems="center">
                                    <Grid item>
                                      {autoTopupAmountLabel !== '' &&
                                      <Tooltip placement='bottom' title={autoTopupAmountLabel}>
                                        <SettingsBackupRestore 
                                          fontSize="small" 
                                          style={{verticalAlign: "-10%"}}
                                          onClick={() => { this.resetAutoTopupAmount(theAccount, theWallet);}}
                                        />
                                      </Tooltip>}
                                    </Grid>
                                    <Grid item>
                                      <CurrencyTextField id="autoTopupAmountPending" className={classes.currencyField3}
                                        value={rAutoTopupAmountValue}
                                        showErrMsg={true}
                                        error={this.isAmountError(theAccount, theWallet)}
                                        errorMsg={this.getAmountErrorMessage(theAccount, theWallet)}
                                        allowNegative={false}
                                        minValue={getTopupTargetMinimum()}
                                        maxValue={getTopupTargetLimit()}
                                        minEnterValue={0}
                                        maxEnterValue={999999}
                                        onChange={(e) => this.setState({ rAutoTopupAmount: e.target.value })}
                                        styleSheet={styleSheet}
                                        textAlign="right"
                                      />
                                    </Grid>
                                  </Grid>}

                                  {! budgetValuesExist &&
                                  <CurrencyTextField id="autoTopupAmountPending" className={classes.currencyField3}
                                    value={rAutoTopupAmountValue}
                                    showErrMsg={true}
                                    error={this.isAmountError(theAccount, theWallet)}
                                    errorMsg={this.getAmountErrorMessage(theAccount, theWallet)}
                                    allowNegative={false}
                                    minValue={getTopupTargetMinimum()}
                                    maxValue={getTopupTargetLimit()}
                                    minEnterValue={0}
                                    maxEnterValue={999999}
                                    onChange={(e) => this.setState({ rAutoTopupAmount: e.target.value })}
                                    styleSheet={styleSheet}
                                    disabled={autoTopupActive === 0}
                                    textAlign="right"
                                  />}
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid item xs={12}>
                              <Paper elevation={1} style={AppBindStyles.tipPaper}>
                                <Grid container spacing={1} direction="row" justifyContent="flex-start">
                                  <Grid item xs={12}>
                                    <div style={{display:"flex"}}>
                                      <Typography variant="body1">
                                        <strong>Tip.</strong>&nbsp;&nbsp;Either you or your customer can refund the balance in their wallet at any time.
                                      </Typography>
                                    </div>
                                  </Grid>      
                                </Grid>
                              </Paper>
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item xs={12}><Divider style={{marginTop:8}}/></Grid>
                        <Grid item xs={12}>
                          <Grid container spacing={1} direction="row" justifyContent="space-between" >
                            <Grid item xs={12}>
                              <Typography variant="h6">
                                Spend controls
                              </Typography>
                            </Grid>
                        
                            <Grid item xs={12}>
                              <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                                <Grid item>
                                  {/* If you set a monthly budget for your customer, we'll decline any subscription charges that would cause your customer to spend more than this budget in a calendar month.*/}
                                  <FormControlLabel
                                      control={
                                        <Switch
                                          checked={useMonthlyBudget === true}
                                          onChange={(e) => this.toggleSetMonthlyBudget(useMonthlyBudget, origrMaxMonthlyBudget, maxMonthlyBudget)}
                                          value={useMonthlyBudget}
                                          color="primary"
                                        />
                                      }
                                      label={<Typography variant="body1" style={AppBindStyles.boldLabel}>Set a monthly spend limit</Typography>}
                                  />
                                </Grid>
                                <Grid item>
                                  <Grid container justifyContent="flex-start" alignItems="center" spacing={1}>
                                    <Grid item>
                                      <Grid container spacing={1} alignItems="center">
                                        <Grid item>
                                          {maxMonthlyBudgetLabel !== '' && rMaxMonthlyBudget != null &&
                                          <Tooltip placement='bottom' title={maxMonthlyBudgetLabel}>
                                            <SettingsBackupRestore 
                                              fontSize="small" 
                                              style={{verticalAlign: "-10%"}}
                                              onClick={() => { this.resetMaxMonthlyBudget(theAccount, theWallet);}}
                                            />
                                          </Tooltip>}
                                          {maxMonthlyBudgetLabel !== '' && rMaxMonthlyBudget == null &&
                                          <Tooltip placement='bottom' title={maxMonthlyBudgetLabel}>
                                            <SettingsBackupRestore 
                                              fontSize="small" 
                                              style={{verticalAlign: "-2%"}}
                                              onClick={() => { this.resetMaxMonthlyBudget(theAccount, theWallet);}}
                                            />
                                          </Tooltip>}
                                        </Grid>
                                        {rMaxMonthlyBudget != null &&
                                        <Grid item>
                                          <CurrencyTextField id="maxMonthlyBudgetPending" className={classes.currencyField3}
                                            value={rMaxMonthlyBudget}
                                            showErrMsg={true}
                                            allowNegative={false}
                                            minValue={getMaxMonthlyBudgetMinimum()}
                                            maxValue={getMaxMonthlyBudgetLimit()}
                                            minEnterValue={0}
                                            maxEnterValue={99999999}
                                            onChange={(e) => this.setState({ rMaxMonthlyBudget: e.target.value, rMaxMonthlyBudgetSet: true }) }
                                            textAlign="right"
                                          />
                                        </Grid>}
                                        {rMaxMonthlyBudget == null &&
                                        <Typography className={classes.noBudgetHolder} variant="body1"><i>No limit</i></Typography>}
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>                    
                    </Paper>
                  </Grid>}

                  {this.props.activeStep === this.props.assignedStep &&
                  <Grid item xs={12}>
                    <Button
                      disabled={this.isBackDisabled()}
                      color="primary" variant="outlined"
                      onClick={() => { this.updateBudget(theAccount, theProject, theWallet, customerSubscriptions.length, "back");}}
                      style={AppBindStyles.bottomButton}>
                      Back
                    </Button>

                    {!this.showSpinner() &&        
                    <Button
                      color="primary" variant="contained"
                      disabled={this.isNextDisabled(theAccount, theProject, theWallet, customerSubscriptions.length, autoTopupActive)}
                      onClick={() => { this.updateBudget(theAccount, theProject, theWallet, customerSubscriptions.length, "next");}}
                      style={AppBindStyles.bottomButton}>
                        Next
                    </Button>}
                    {this.showSpinner() &&
                    <Button color="primary" 
                            variant="contained"
                            style={AppBindStyles.bottomButton}>
                        <SavingSpinner/>
                    </Button>}
                  </Grid>} 
                </Grid>       
              );
          
            } else {
              let rMaxMonthlyBudgetL = 'No limit';
              if (rMaxMonthlyBudget !== null) {
                rMaxMonthlyBudgetL = Number(rMaxMonthlyBudget).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}); 
              }

              let oneTimeFeeRequestL = 'No request';
              if (initialBalanceAmount !== null) {
                oneTimeFeeRequestL = Number(initialBalanceAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}); 
              }

              let initialBalanceAmountL = Number(initialBalanceAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
              let rAutoTopupAmountL     = Number(rAutoTopupAmount).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}); 
              let rAutoTopupThresholdL  = Number(rAutoTopupThreshold).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2}); 

              //display only
              return ( 
                <Grid container spacing={2} >
                  {(customerSubscriptions.length > 0 || theAccount.rPreApproveSubs === 1 || theAccount.preApproveSubs === 1) &&
                  <Grid item xs={12}>
                    <Grid container spacing={2} direction="row" justifyContent="space-between" >
                      <Grid item xs={12}>
                        <Typography variant="h6">
                          Wallet for subscriptions expensed to {theAccountName}
                        </Typography>
                        {/* Edge case - no subscriptions */}
                        {customerSubscriptions.length === 0 &&
                        <Typography>{customerAccordionTitle1a}</Typography>}
                      </Grid>

                      {customerSubscriptions.length > 0 &&
                      <Grid item xs={12}>
                        <Accordion>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel1a-content"
                            id="panel1a-header"
                          >
                            <Typography>
                              {customerAccordionTitleDo1}&nbsp;
                              <span style={{color: "blue", textDecoration: "underline"}}>{customerAccordionTitle2}</span>
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            {customerSubscriptions.length > 0 &&
                            <TableContainer>
                              <StyledTable>
                                <TableHead>
                                  <TableRow>
                                    <StyledTableCell sx={{ borderTopLeftRadius: 8}}/>
                                    <StyledTableCell>Subscription</StyledTableCell>
                                    <StyledTableCell>Terms</StyledTableCell>
                                    <StyledTableCell sx={{ borderTopRightRadius: 8}}/>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {customerSubscriptions.map(n => {
                                    return (
                                      <SubscriptionTableRow 
                                        key={n.customerAppId}
                                        subscription={n}
                                        styledTableRow={StyledTableRow}
                                        styledTableCell={StyledTableCell}
                                        editable={false}
                                        showModel={false}
                                      />
                                    );
                                  })}
                                </TableBody>
                              </StyledTable>
                            </TableContainer>}
                          </AccordionDetails>
                        </Accordion>
                      </Grid>}

                      <Grid item xs={12}>
                        <Grid container spacing={1} direction="row" justifyContent="space-between" >

                          {/* Only show initially fund on first time use - that is, until a budget has been accepted */}
                          {budgetApprovedCount === 0 && initialBalanceAmount != null && initialBalanceAmount > 0 &&
                          <Grid item xs={12}>
                            <Grid container spacing={2} direction="row" justifyContent="space-between" >
                              <Grid item>
                                <Typography variant="body1" style={AppBindStyles.boldLabel}>Initially fund balance</Typography>
                              </Grid>
                              <Grid item>
                                <Typography>{initialBalanceAmountL}</Typography>
                              </Grid>
                            </Grid>
                          </Grid>}
                          {/* useOneTimeFeeRequest === true */}
                          {budgetApprovedCount > 0 && initialBalanceAmount != null && initialBalanceAmount > 0 &&
                          <Grid item xs={12}>
                            <Grid container spacing={2} direction="row" justifyContent="space-between" >
                              <Grid item>
                                <Typography variant="body1" style={AppBindStyles.boldLabel}>Add funds now</Typography>
                              </Grid>
                              <Grid item>
                                <Typography>{oneTimeFeeRequestL}</Typography>
                              </Grid>
                            </Grid>
                          </Grid>}

                          {autoTopupActive === 1 &&
                          <Grid item xs={12}>
                            <Grid container spacing={2} direction="row" justifyContent="space-between" >
                              <Grid item>
                                <Typography variant="body1" style={AppBindStyles.boldLabel}>When balance falls below</Typography>
                              </Grid>
                              <Grid item>
                                <Grid container spacing={1} alignItems="center">
                                  <Grid item>
                                    {autoTopupThresholdDisplayOnlyLabel !== '' &&
                                    <Tooltip placement='bottom' title={autoTopupThresholdDisplayOnlyLabel}>
                                      <SettingsBackupRestore 
                                        fontSize="small" 
                                        style={{verticalAlign: "-10%"}}
                                      />
                                    </Tooltip>}
                                  </Grid>
                                  <Grid item>
                                    <Typography>{rAutoTopupThresholdL}</Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>}
                          {autoTopupActive === 1 &&
                          <Grid item xs={12}>
                            <Grid container spacing={2} direction="row" justifyContent="space-between" >
                              <Grid item>
                                <Typography variant="body1" style={AppBindStyles.boldLabel}>Fund balance to</Typography>
                              </Grid>
                              <Grid item>
                                <Grid container spacing={1} alignItems="center">
                                  <Grid item>
                                    {autoTopupAmountDisplayOnlyLabel !== '' &&
                                    <Tooltip placement='bottom' title={autoTopupAmountDisplayOnlyLabel}>
                                      <SettingsBackupRestore 
                                        fontSize="small" 
                                        style={{verticalAlign: "-10%"}}
                                      />
                                    </Tooltip>}
                                  </Grid>
                                  <Grid item>
                                    <Typography>{rAutoTopupAmountL}</Typography>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>}

                          <Grid item xs={12}>
                            <Grid container spacing={2} direction="row" justifyContent="space-between" >
                              <Grid item>
                                <Typography variant="body1" style={AppBindStyles.boldLabel}>Limit monthly spend to</Typography>
                              </Grid>
                              <Grid item>
                                <Grid container spacing={1} alignItems="center">
                                  <Grid item>
                                    {maxMonthlyBudgetDisplayOnlyLabel !== '' &&
                                    <Tooltip placement='bottom' title={maxMonthlyBudgetDisplayOnlyLabel}>
                                      <SettingsBackupRestore 
                                        fontSize="small" 
                                        style={{verticalAlign: "-10%"}}
                                      />
                                    </Tooltip>}
                                  </Grid>
                                  <Grid item>
                                    {rMaxMonthlyBudgetL === 'No limit' &&
                                    <Typography variant="body1"><i>No limit</i></Typography>}
                                    {rMaxMonthlyBudgetL !== 'No limit' &&
                                    <Typography variant="body1">{rMaxMonthlyBudgetL}</Typography>}
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>}

                </Grid>       
           
              );        
            }            
          }
          if (!displayOnly) {
            return <div><LoadingSpinner/></div>
          }
        }
      }
      />
    );
  }
}

StepBudgetCustomer.propTypes = {
  assignedStep: PropTypes.number.isRequired,
  activeStep: PropTypes.number.isRequired,
  handleBack: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired
};

export default withStyles(styleSheet)(StepBudgetCustomer);
